import type { VoidFunctionComponent } from 'react';
import * as cx from 'classnames';
import Tooltip from '../Tooltip';
import styles from './CopiableText.module.less';
import CopyToClipboard from '../CopyToClipboard';
import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard';

type Props = {
  /**
   * Name of the text to copy (e.g. "locator" or "CPE").
   */
  name: string;
  /**
   * Text to copy to clipboard.
   */
  value: string;
  /**
   * Unique identifier for the tooltip.
   */
  id: string;
  /**
   * Whether to hide the sensitive value (default: false).
   */
  hideSensitiveValue?: boolean;
  /**
   * The variant of the component (default: 'default').
   */
  variant?: 'default' | 'primary';
  /**
   * Whether to show the copy icon (default: true).
   */
  showCopyIcon?: boolean;
};

/**
 * Component to display a copiable text with a tooltip.
 */
export const CopiableText: VoidFunctionComponent<Props> = ({
  name,
  value,
  id,
  variant = 'default',
  hideSensitiveValue = false,
  showCopyIcon = true,
}) => {
  const [isCopied, setIsCopied] = useCopyToClipboard();

  const tooltipText = (
    <div className={styles.tooltip}>
      <span>{!isCopied ? `Click to copy ${name}` : `${name} copied!`}</span>
      <br />
      {!hideSensitiveValue && value}
    </div>
  );

  return (
    <CopyToClipboard text={value} onCopy={() => setIsCopied(true)} showCopyIcon={showCopyIcon}>
      <Tooltip id={`${id}-${value}`} tooltip={tooltipText}>
        <span className={cx({ [styles.value]: variant === 'default', [styles.valuePrimary]: variant === 'primary' })}>
          {hideSensitiveValue ? `Copy ${name}` : value}
        </span>
      </Tooltip>
    </CopyToClipboard>
  );
};
